import { useMemo, type JSX } from 'react';

import type { Blueprint } from '@rta/rta-blueprint-library';
import { ComponentController, DisplayMode } from '@r-core';
import { RtaBlueprintComponent } from '../app/rta-blueprint-component';

// Overhead labels scale from 1.8 (small kitchens) up to 2.0 (large kitchens): larger footprints
// zoom out further, shrinking labels, so they need a bigger factor to stay legible.
const MIN_FONT_FACTOR = 1.8;
const MAX_FONT_FACTOR = 2;
const SMALL_KITCHEN_FT = 6;
const LARGE_KITCHEN_FT = 20;

export type CreateOverheadControllerOptions = {
	assetsBaseUrl?: string;
	eventGroup?: string;
};

/**
 * Builds a read-only `ComponentController` framed for the dimensioned overhead view: readonly, no
 * context menu, no state/title chrome, `DisplayMode.Overhead`, with size-scaled dimension labels.
 * Shared by the headless PNG capture and the on-screen `ReadonlyOverheadView`.
 * @param blueprint the design to render
 * @param options optional assets base URL and a custom event group for isolation
 */
export const createOverheadController = (blueprint: Blueprint, options: CreateOverheadControllerOptions = {}): ComponentController => {
	const { assetsBaseUrl, eventGroup = `overhead-${blueprint.id}` } = options;

	const controller = new ComponentController(blueprint, { eventGroup });
	if (assetsBaseUrl) controller.options.assetsBaseUrl = assetsBaseUrl;
	controller.options.contextMenu = false;
	controller.options.readonly = true;
	controller.options.showDeveloperInsights = false;
	controller.options.showState = false;
	controller.options.showTitle = false;
	controller.navigation.displayMode = DisplayMode.Overhead;

	// Overhead mode resets dimensionFontSizeFactor to 1.2 (navigation.class.ts); override it here so
	// the presentation overhead gets the larger, size-scaled labels.
	const size = controller.kitchen?.size;
	const extent = size ? Math.max(size.width, size.depth) : 0;
	const t = Math.min(Math.max((extent - SMALL_KITCHEN_FT) / (LARGE_KITCHEN_FT - SMALL_KITCHEN_FT), 0), 1);
	controller.options.dimensionFontSizeFactor = MIN_FONT_FACTOR + t * (MAX_FONT_FACTOR - MIN_FONT_FACTOR);

	return controller;
};

export type ReadonlyOverheadViewProps = {
	blueprint: Blueprint;
	assetsBaseUrl?: string;
	backgroundColor?: string;
};

/**
 * On-screen, read-only dimensioned overhead of a blueprint. Fills its parent — give the parent a
 * height. Renders the same view the headless capture rasterizes, so the customer sees live the exact
 * overhead a saved PNG would have shown.
 */
export const ReadonlyOverheadView = (props: ReadonlyOverheadViewProps): JSX.Element => {
	const { blueprint, assetsBaseUrl, backgroundColor = '#FFFFFF' } = props;

	const controller = useMemo(
		() => createOverheadController(blueprint, { assetsBaseUrl, eventGroup: `overhead-view-${blueprint.id}` }),
		[blueprint, assetsBaseUrl]
	);

	return <RtaBlueprintComponent controller={controller} backgroundColor={backgroundColor} />;
};
